import { useEffect, useMemo, useRef, useState } from 'react';
import { ActivationStatsStageData } from '../ActivationSyncHistory/models/stats-data';
import {
  ACTIVATION_STATS_DEFAULT_TIME_FILTER,
  ACTIVATION_STATS_POLL_PERIOD,
  ACTIVATION_STATS_TIME_FILTER_KEY,
  ACTIVATION_STATS_VIEW_FILTER_OPTIONS
} from './constant';
import { EntityUIState } from '../../../core/interfaces/entitiy-ui-state';
import { SettingsStorageService } from '../../../auth/services/settings-storage.service';
import { GraphConstants } from '../../../chart/constants/eventBarGraph';
import { useQuery } from '../../../core/hooks/useQuery';
import useService from '../../../injector/hooks/useService';
import ActivationsAPI from '../api/ActivationsAPI';

export const useActivationStats = (activationId) => {
  const _settingsStorageService = useService(SettingsStorageService);
  const timeFilter = useRef<any>(null);
  const noOfBars = GraphConstants.activationEventsBarCount;
  const [ statsFilter, setStatsFilter ] = useState(ACTIVATION_STATS_VIEW_FILTER_OPTIONS[0]);

  const {
    uiState: loadStatsUIState,
    data: loadStats,
    fetch: fetchLoadStats,
    refetch: refetchLoadStats
  } = useQuery({
    dataSource$: () => {
      return ActivationsAPI.getActivationRunStats(
        activationId,
        timeFilter.current,
        noOfBars
      ).then((res) => res);
    },
    pollInterval: ACTIVATION_STATS_POLL_PERIOD,
    emptyAssertion: (data) => !data
  });

  const loadingStates = [ EntityUIState.LOADING, EntityUIState.REFRESHING ];
  const fetchingAll = loadingStates.includes(loadStatsUIState);
  const initialLoadStatsFetched = loadStatsUIState === EntityUIState.IDLE || loadStatsUIState === EntityUIState.REFRESHING;
  const stats = useMemo(() => getFilteredStats(), [ loadStats, statsFilter ]);

  useEffect(() => {
    const cachedValue: number = _getTimeFilterFromCache();
    timeFilter.current = cachedValue || ACTIVATION_STATS_DEFAULT_TIME_FILTER;

    fetchLoadStats();
  }, [ activationId ]);

  const onStatusFilterChange = (newFilterValue) => {
    setStatsFilter(newFilterValue);
  };

  const onRefreshClick = () => {
    refetchLoadStats();
  };

  const onTimeFilterSelect = (newTimeFilter) => {
    timeFilter.current = newTimeFilter.value;

    _saveTimeFilterInCache(newTimeFilter.value);
    refetchLoadStats();
  };

  const _saveTimeFilterInCache = (value: number) => {
    _settingsStorageService.applySettings(
      `activation.${ activationId }.${ ACTIVATION_STATS_TIME_FILTER_KEY }`,
      value
    );
  };

  const _getTimeFilterFromCache = () => {
    return _settingsStorageService.getSettings(
      `activation.${ activationId }.${ ACTIVATION_STATS_TIME_FILTER_KEY }`
    );
  };

  function getFilteredStats() {
    let statsData = loadStats;

    if (statsFilter.value === ACTIVATION_STATS_VIEW_FILTER_OPTIONS[1].value) {
      statsData = filterSuccessEvents(loadStats);
    } else if (statsFilter.value === ACTIVATION_STATS_VIEW_FILTER_OPTIONS[2].value) {
      statsData = filterFailedEvents(loadStats);
    }

    return statsData;
  }

  function filterSuccessEvents(statsStageData: ActivationStatsStageData[]) {
    return statsStageData.map((stageData) => ({
      ...stageData,
      dataPoints: stageData.dataPoints.map((dataPoint) => ({
        ...dataPoint,
        failed: 0
      }))
    }));
  }

  function filterFailedEvents(statsStageData: ActivationStatsStageData[]) {
    return statsStageData.map((stageData) => ({
      ...stageData,
      dataPoints: stageData.dataPoints.map((dataPoint) => ({
        ...dataPoint,
        total: 0
      }))
    }));
  }

  return {
    noOfBars,
    stats,
    timeFilter: timeFilter.current,
    loadStatsUIState,
    fetchingAll,
    onTimeFilterSelect,
    onRefreshClick,
    initialLoadStatsFetched,
    statsFilter,
    onStatusFilterChange
  };
};

